import { useId, useRef, useState, type FormEvent, type JSX } from "react";
import { TAG_NAME_MAX, type BulkAction, type BulkActionBody, type BulkActionResponse, type BulkTarget } from "@gioia365/contracts";
import type { Locale } from "@gioia365/i18n";
import { useQuery } from "@tanstack/react-query";
import { ApiRequestError } from "../../api/http";
import { Button } from "../../design/components/Button";
import { Dialog } from "../../design/components/Dialog";
import { Select } from "../../design/components/Select";
import { TextArea } from "../../design/components/TextArea";
import { TextField } from "../../design/components/TextField";
import { messageForError } from "../../i18n/errors";
import { formatCount } from "../../i18n/format";
import { useLocale, useT, type Translate } from "../../i18n/locale";
import type { TabContext } from "../../session/tab-context";
import { leadOwnerOptionsQuery, leadStatusesQuery, tagsQuery, useCreateTag } from "../lead-queries";
import { newTagName, tagsMatching } from "../new-tag";
import { useBulkAction } from "./bulk-queries";
import type { PickedLead } from "./bulk-selection";
import { allResultsText, confirmText, createAndAddText, summaryText, tagCountText } from "./bulk-text";
import "./bulk.css";

// La finestra di un'azione multipla (piano M3 Task 14; mock-up `lead.html`, `m-bulk-dialog`): la scelta (nuovo owner
// con il motivo obbligatorio, nuovo status, il tag da aggiungere o da togliere; niente per «Archivia», che spiega dove
// ritrovarli), il riepilogo dei Lead che riguarda e la conferma con il loro numero. Il gruppo è fissato quando si apre
// la finestra: gli id spuntati, oppure la vista di adesso con il numero visto; il server lo risolve alla conferma.
// I valori si scelgono fra quelli che il server accetta: le persone che possono essere owner, gli status che si
// scelgono, i tag non nascosti. «Togli tag» offre i tag che hanno i Lead spuntati, con quanti ce l'hanno; con «tutti i
// risultati» le righe non sono tutte caricate, quindi offre tutti i tag dell'azienda e lo dice. Un rifiuto non ha
// scritto nulla: si legge nella finestra, che resta aperta (una finestra modale rende inerte la pagina, avvisi compresi).
// «Aggiungi tag» con `canCreateTags` (24/10/2026) ha la casella «Cerca o crea un tag»: filtra i tag e, per un nome che non c'è,
// propone «Crea il tag «…»», che crea il tag (`POST /api/tags`) e lo aggiunge subito al gruppo, come la conferma.

/** Il valore di «Senza owner» nel selettore (un identificativo non è mai vuoto). */
const NO_OWNER = "";

type Option = { value: string; label: string };

type Props = {
  tab: TabContext;
  action: BulkAction;
  target: BulkTarget;
  /** I Lead spuntati, nell'ordine in cui li si è spuntati; vuoto con «tutti i risultati». */
  picked: readonly PickedLead[];
  /** Chi agisce vede più Lead di quanti ne modifica: il server lascia fuori gli altri, e la finestra lo dice prima. */
  leftOutPossible: boolean;
  onDone: (body: BulkActionBody, response: BulkActionResponse) => void;
  onClose: () => void;
  /** Se «Aggiungi tag» può creare un tag nuovo (`manage_tags`). */
  canCreateTags?: boolean;
};

/** I tag dei Lead spuntati, per nome, ognuno con quanti Lead ce l'hanno. */
function pickedTagOptions(t: Translate, locale: Locale, picked: readonly PickedLead[]): Option[] {
  const counts = new Map<string, { name: string; count: number }>();
  for (const lead of picked) {
    for (const tag of lead.tags) {
      const entry = counts.get(tag.id);
      counts.set(tag.id, { name: tag.name, count: (entry?.count ?? 0) + 1 });
    }
  }
  return [...counts.entries()]
    .sort(([, a], [, b]) => a.name.localeCompare(b.name, locale))
    .map(([id, entry]) => ({ value: id, label: tagCountText(t, locale, entry.name, entry.count, picked.length) }));
}

export function BulkDialog({ tab, action, target, picked, leftOutPossible, onDone, onClose, canCreateTags = false }: Props): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const formId = useId();
  const reasonId = `${formId}-reason`;
  const reasonRef = useRef<HTMLTextAreaElement>(null);
  const bulk = useBulkAction(tab);
  const all = target.kind === "query";
  const count = all ? target.expectedTotal : picked.length;

  const owners = useQuery({ ...leadOwnerOptionsQuery(tab), enabled: action === "change_owner" });
  const statuses = useQuery({ ...leadStatusesQuery(tab), enabled: action === "change_status" });
  const companyTags = action === "add_tag" || (action === "remove_tag" && all);
  const tags = useQuery({ ...tagsQuery(tab), enabled: companyTags });
  const createTag = useCreateTag(tab);
  const searchable = action === "add_tag" && canCreateTags;
  const [tagSearch, setTagSearch] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);

  const [chosen, setChosen] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [valueError, setValueError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Le voci della scelta e la lettura da cui arrivano (`null` quando non ce n'è una da aspettare).
  let options: Option[] = [];
  let source: typeof owners | typeof statuses | typeof tags | null = null;
  if (action === "change_owner") {
    source = owners;
    if (owners.data) options = [...owners.data.map((owner) => ({ value: owner.membershipId, label: owner.displayName })), { value: NO_OWNER, label: t("bulkActions.noOwner") }];
  } else if (action === "change_status") {
    source = statuses;
    options = (statuses.data ?? []).filter((status) => status.selectable).map((status) => ({ value: status.id, label: status.name }));
  } else if (companyTags) {
    source = tags;
    // Senza maiuscole, accenti e segni, come il confronto dei doppioni: un tag che c'è già resta sempre fra le voci.
    options = tagsMatching(tags.data ?? [], searchable ? tagSearch : "").map((tag) => ({ value: tag.id, label: tag.name }));
  } else if (action === "remove_tag") {
    options = pickedTagOptions(t, locale, picked);
  }
  const value = chosen !== null && options.some((option) => option.value === chosen) ? chosen : (options[0]?.value ?? null);
  const ready = action === "archive" || value !== null;
  const pending = bulk.isPending || createTag.isPending;
  const candidate = searchable && tags.isSuccess ? newTagName(tagSearch, tags.data) : null;

  function bodyOf(chosenValue: string | null): BulkActionBody | null {
    switch (action) {
      case "change_owner":
        return chosenValue === null ? null : { action, target, ownerMembershipId: chosenValue === NO_OWNER ? null : chosenValue, reason: reason.trim() };
      case "change_status":
        return chosenValue === null ? null : { action, target, statusId: chosenValue };
      case "add_tag":
      case "remove_tag":
        return chosenValue === null ? null : { action, target, tagId: chosenValue };
      case "archive":
        return { action, target };
    }
  }

  // «Crea il tag «…» e aggiungilo a N Lead»: il tag nuovo (o quello nato nel frattempo con lo stesso nome) si aggiunge subito al
  // gruppo fissato. Se l'aggiunta non riesce, il tag resta creato e scelto (già nella cache dei tag) e il focus torna sulla casella.
  async function handleCreate(name: string) {
    if (pending) return;
    setFormError(null);
    setValueError(null);
    let tagId: string;
    try {
      tagId = (await createTag.mutateAsync(name)).tag.id;
    } catch (error) {
      setFormError(messageForError(t, error));
      return;
    }
    setTagSearch("");
    setChosen(tagId);
    if (!(await apply({ action: "add_tag", target, tagId }))) searchRef.current?.focus();
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending || !ready) return;
    setFormError(null);
    setValueError(null);
    if (action === "change_owner" && !reason.trim()) {
      setReasonError(t("bulkActions.reasonRequired"));
      reasonRef.current?.focus();
      return;
    }
    setReasonError(null);
    const body = bodyOf(value);
    if (!body) return;
    await apply(body);
  }

  /** L'azione al server; `false` se non è riuscita (il motivo è già nella finestra). */
  async function apply(body: BulkActionBody): Promise<boolean> {
    try {
      const response = await bulk.mutateAsync(body);
      onDone(body, response);
      return true;
    } catch (error) {
      if (!(error instanceof ApiRequestError)) {
        setFormError(messageForError(t, error));
      } else if (error.code === "reason_required") {
        setReasonError(t("bulkActions.reasonRequired"));
        reasonRef.current?.focus();
      } else if (error.code === "owner_not_eligible" || error.code === "invalid_field_value") {
        // La scelta non vale più (una persona sospesa, uno status o un tag tolti nel frattempo): le voci si rileggono.
        setValueError(messageForError(t, error));
        void source?.refetch();
      } else if (error.code === "bulk_too_large" && typeof error.details?.count === "number") {
        setFormError(t("bulkActions.tooLarge", { count: formatCount(locale, error.details.count) }));
      } else {
        setFormError(messageForError(t, error));
      }
      return false;
    }
  }

  const summary = all ? allResultsText(t, locale, count) : summaryText(t, locale, picked.map((lead) => lead.name));

  let label: string | null = null;
  if (action === "change_owner") label = t("bulkActions.newOwner");
  else if (action === "change_status") label = t("bulkActions.newStatus");
  else if (action === "add_tag") label = t("bulkActions.tagToAdd");
  else if (action === "remove_tag") label = t("bulkActions.tagToRemove");

  let field: JSX.Element | null = null;
  if (action === "archive") {
    field = <p>{t("bulkActions.archiveNote")}</p>;
  } else if (source?.isError) {
    field = (
      <p className="g-field__error" role="alert">
        {messageForError(t, source.error)}
      </p>
    );
  } else if (source?.isPending) {
    field = <p className="g-field__hint">{t("common.loading")}</p>;
  } else if (options.length === 0) {
    let empty: string;
    if (searchable && tagSearch.trim() !== "") empty = t("bulkActions.tagNoMatch", { q: tagSearch.trim() });
    else if (action === "change_status") empty = t("bulkActions.noStatuses");
    else if (action === "remove_tag" && !all) empty = t("bulkActions.noTags");
    else empty = t("leadCard.tagsDialog.empty");
    field = <p>{empty}</p>;
  } else if (label !== null && value !== null) {
    field = (
      <Select
        id={`${formId}-value`}
        label={label}
        value={value}
        onChange={(next) => {
          setChosen(next);
          setValueError(null);
        }}
        options={options}
        hint={action === "remove_tag" && all ? t("bulkActions.tagsAllResults") : undefined}
        error={valueError ?? undefined}
      />
    );
  }

  return (
    <Dialog
      open
      title={t(`bulkActions.actions.${action}`)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t("common.cancel")}</Button>
          <Button variant="primary" type="submit" form={formId} disabled={!ready || pending} aria-busy={pending ? "true" : undefined}>
            {confirmText(t, locale, action, count)}
          </Button>
        </>
      }
    >
      <form id={formId} className="g-stack g-bulk-dialog" onSubmit={(event) => void handleSubmit(event)} aria-busy={pending} noValidate>
        {searchable && tags.isSuccess ? (
          <div className="g-field">
            <TextField
              ref={searchRef}
              id={`${formId}-search`}
              label={t("bulkActions.tagSearch")}
              type="search"
              autoComplete="off"
              maxLength={TAG_NAME_MAX}
              value={tagSearch}
              onChange={(event) => {
                setTagSearch(event.target.value);
                setValueError(null);
              }}
              onKeyDown={(event) => {
                // Invio nella casella non conferma l'azione: crea il tag soltanto se nessun tag che c'è risponde a ciò che è scritto.
                if (event.key !== "Enter") return;
                event.preventDefault();
                if (candidate !== null && options.length === 0) void handleCreate(candidate);
              }}
            />
            {/* La voce compare mentre si scrive: la regione la annuncia. */}
            <div aria-live="polite">
              {candidate !== null ? (
                <Button onClick={() => void handleCreate(candidate)} disabled={pending}>
                  {createAndAddText(t, locale, candidate, count)}
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
        {field}
        {action === "change_owner" ? (
          <TextArea
            ref={reasonRef}
            id={reasonId}
            label={t("bulkActions.reason")}
            className="g-bulk-dialog__reason"
            rows={3}
            maxLength={500}
            value={reason}
            placeholder={t("bulkActions.reasonPlaceholder")}
            error={reasonError ?? undefined}
            onChange={(event) => setReason(event.target.value)}
          />
        ) : null}
        <p className="g-bulk-dialog__summary">{summary}</p>
        {leftOutPossible ? <p className="g-field__hint">{t("bulkActions.onlyEditable")}</p> : null}
        <p className="g-field__hint">{t("bulkActions.fixedNote")}</p>
        {formError ? (
          <p className="g-field__error" role="alert">
            {formError}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
